import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useCurrency } from '../contexts/CurrencyContext';
import BrandName from '../components/BrandName';
import MainNavigation from '../components/MainNavigation';
import Footer from '../components/Footer';
import ThreeDPreview from '../components/ThreeDPreview';
import LoginModal from '../components/LoginModal';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { Upload, Package, Zap, Shield, Settings, Check, ChevronRight, Printer, FileText, DollarSign, Boxes, ShoppingCart, Eye, User, Edit, Users, Mail, Phone, MapPin, Clock, Star, Heart, X, CheckCircle, AlertCircle, Info, Truck, CreditCard, LogIn, Menu, Search, Filter, Download, Home, BookOpen, HelpCircle, FileQuestion, Palette, ArrowLeft } from 'lucide-react';
import { trackQuoteSubmission, trackCheckout, trackPageView } from '../utils/analytics';
import { BACKEND_URL } from '../config';

const API = `${BACKEND_URL}/api`;


const ComingSoonPage = ({ onAuthenticate }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    // Get password from environment or use default
    const correctPassword = process.env.REACT_APP_SITE_PASSWORD || 'launch2025';
    
    if (password === correctPassword) {
      sessionStorage.setItem('siteAuthenticated', 'true');
      onAuthenticate();
    } else {
      setError('Incorrect password. Please try again.');
      setPassword('');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4 py-12">
      <div className="max-w-2xl w-full">
        <div className="text-center mb-8">
          <h1 className="text-white mb-4">
            <BrandName size="3xl" className="uppercase" />
          </h1>
          <p className="text-xl text-blue-200 mb-2">Coming Soon</p>
          <p className="text-slate-300">
            We're putting the final touches on something amazing. Enter the password to preview.
          </p>
        </div>

        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-white mb-2">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError('');
                }}
                className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter password"
                required
              />
            </div>

            {error && (
              <div className="bg-red-500/20 border border-red-500/50 text-red-200 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              className="w-full bg-[#0007b5] hover:bg-[#000690] text-white font-semibold py-3 rounded-lg transition-colors"
            >
              Enter Site
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-white/20 text-center">
            <p className="text-sm text-slate-300">
              Don't have the password? <a href="mailto:hello@create3dworks.com" className="text-blue-400 hover:text-blue-300">Contact us</a>
            </p>
          </div>
        </div>

        {/* Professional Designer Invitation Card */}
        <div className="mt-8 bg-gradient-to-br from-blue-600/20 to-purple-600/20 backdrop-blur-md rounded-2xl p-6 border border-blue-400/30">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
                <Palette className="w-6 h-6 text-blue-300" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-white mb-2">
                Professional 3D Designers Welcome
              </h3>
              <p className="text-sm text-slate-300 mb-4">
                Join our marketplace and showcase your 3D designs to customers worldwide. Earn revenue from every sale.
              </p>
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-200 border border-blue-400/30">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Passive Income
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-500/20 text-purple-200 border border-purple-400/30">
                  <Users className="w-3 h-3 mr-1" />
                  Global Reach
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-200 border border-green-400/30">
                  <Shield className="w-3 h-3 mr-1" />
                  IP Protected
                </span>
              </div>
              <a
                href="mailto:designers@create3dworks.com?subject=Designer Application Inquiry"
                className="inline-flex items-center px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-lg transition-colors"
              >
                Apply as Designer
                <ChevronRight className="w-4 h-4 ml-1" />
              </a>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center text-slate-400 text-sm">
          <p>© 2025 <span className="brand-name"><span className="brand-name-bold">CREATE 3D</span> Works</span>. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};


const AppContent = () => {
  const navigate = useNavigate();
  const { cart, addToCart, clearCart } = useCart();
  const { user, isAuthenticated, loginWithGoogle, logout, loading, getIdToken } = useAuth();
  const { formatPrice } = useCurrency();
  
  const [activeTab, setActiveTab] = useState("upload");
  const [quoteData, setQuoteData] = useState(null);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(null);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentError, setPaymentError] = useState(null);
  const [formData, setFormData] = useState({
    file: null,
    material: "",
    color: "Any",
    customColor: "",
    quality: "",
    strength: "",
    smoothing: "",
    contact_email: "",
    notes: "",
    units: "mm",  // Default to millimeters
    country_code: "CA",  // Default shipping to Canada (domestic)
    postal_code: "",  // Optional postal/zip code
    existing_quote_id: null,  // For preserving file when editing quote
    preserved_filename: null  // Filename of preserved file when editing
  });

  // Handle hash-based navigation on page load
  React.useEffect(() => {
    const hash = window.location.hash.substring(1);
    if (hash) {
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - 100;
          window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        }
      }, 100);
    }
  }, []);

  // Check for payment status on page load
  React.useEffect(() => {
    // Wait for Firebase auth to finish loading
    if (loading) return;
    
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const paymentParam = urlParams.get('payment');

    if (sessionId && paymentParam === 'success') {
      // User returned from successful Stripe checkout
      if (isAuthenticated) {
        setPaymentStatus('checking');
        pollPaymentStatus(sessionId);
      } else {
        // User is not authenticated, might have been logged out during payment
        setPaymentError('Please log in to check your payment status.');
      }
    } else if (paymentParam === 'cancelled') {
      setPaymentError('Payment was cancelled. You can try again anytime.');
    }
  }, [loading, isAuthenticated]); // Add dependencies to wait for auth state

  // Function to poll payment status
  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 3000; // 3 seconds

    if (attempts >= maxAttempts) {
      setPaymentStatus('timeout');
      setPaymentError('Payment verification timed out. Please check your email or profile for order status.');
      return;
    }

    try {
      // Get Firebase ID token for authenticated request
      const idToken = await getIdToken();
      if (!idToken) {
        // If we can't get the token, wait a bit and retry (in case auth is still loading)
        if (attempts < 3) {
          console.log(`Waiting for authentication... attempt ${attempts + 1}`);
          setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
          return;
        } else {
          setPaymentStatus('error');
          setPaymentError('Unable to verify authentication. Please log in and check your profile for order status.');
          return;
        }
      }

      const response = await fetch(`${API}/payments/status/${sessionId}`, {
        headers: {
          'Authorization': `Bearer ${idToken}`
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.payment_status === 'paid') {
          setPaymentStatus('success');
          setPaymentError(null);
          
          // Track order completion in Google Analytics
          if (data.order_id && data.total_amount) {
            trackOrderCreation({
              order_id: data.order_id,
              total_price: data.total_amount,
              quote_id: data.quote_id,
              item_name: data.item_name || 'Print Order'
            });
          }
          
          // Clear the cart after successful payment
          clearCart();
          console.log('Payment successful - cart cleared');
          // Clear URL parameters
          window.history.replaceState({}, document.title, window.location.pathname);
          return;
        } else if (data.status === 'expired') {
          setPaymentStatus('failed');
          setPaymentError('Payment session expired. Please try again.');
          return;
        }

        // If payment is still pending, continue polling
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        throw new Error('Failed to check payment status');
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPaymentStatus('error');
      setPaymentError('Error checking payment status. Please check your profile for order updates.');
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    // Clear preserved filename when a new file is uploaded
    setFormData({ ...formData, file, preserved_filename: null });
  };

  const scrollToQuoteSection = () => {
    setActiveTab("upload");
    setTimeout(() => {
      const quoteSection = document.querySelector('[data-section="quote-interface"]');
      if (quoteSection) {
        quoteSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleFooterLinkClick = (section) => {
    const element = document.getElementById(section);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const scrollToQuoteAndSetTab = (tabName) => {
    setActiveTab(tabName);
    setTimeout(() => {
      const quoteSection = document.querySelector('[data-section="quote-interface"]');
      if (quoteSection) {
        quoteSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const navigateToPage = (page) => {
    navigate(`/${page}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navigateToProduct = (product) => {
    const slug = createProductSlug(product.manufacturer, product.name);
    navigate(`/product/${slug}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navigateToProducts = () => {
    navigate('/products');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navigateToCart = () => {
    navigate('/cart');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navigateHome = () => {
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    
    // Validate that either a file is uploaded OR we have an existing_quote_id
    if (!formData.file && !formData.existing_quote_id) {
      alert('Please upload a 3D model file');
      return;
    }
    
    setLoadingQuote(true);
    
    try {
      const formDataObj = new FormData();
      
      // Only append file if a new one is uploaded
      if (formData.file) {
        formDataObj.append('file', formData.file);
      }
      
      // If editing an existing quote, include the quote_id
      if (formData.existing_quote_id) {
        formDataObj.append('existing_quote_id', formData.existing_quote_id);
      }
      
      formDataObj.append('material', formData.material);
      formDataObj.append('color', formData.color === 'Other' ? formData.customColor : formData.color);
      formDataObj.append('quality', formData.quality);
      formDataObj.append('strength', formData.strength);
      formDataObj.append('smoothing', formData.smoothing);
      formDataObj.append('contact_email', formData.contact_email);
      formDataObj.append('units', formData.units);  // Add units to submission
      formDataObj.append('country_code', formData.country_code);  // Add shipping destination
      if (formData.postal_code) formDataObj.append('postal_code', formData.postal_code);  // Add postal code if provided
      if (formData.notes) formDataObj.append('notes', formData.notes);
      
      // Add dimensions for accurate shipping calculation (if available from 3D analysis)
      if (formData.analysisData && formData.analysisData.dimensions) {
        formDataObj.append('dimension_x', parseFloat(formData.analysisData.dimensions.x));
        formDataObj.append('dimension_y', parseFloat(formData.analysisData.dimensions.y));
        formDataObj.append('dimension_z', parseFloat(formData.analysisData.dimensions.z));
        console.log('Sending dimensions for shipping calculation (cm):', {
          x: parseFloat(formData.analysisData.dimensions.x),
          y: parseFloat(formData.analysisData.dimensions.y),
          z: parseFloat(formData.analysisData.dimensions.z)
        });
      }

      const response = await fetch(`${API}/quote/3d-print`, {
        method: 'POST',
        body: formDataObj,
      });

      if (response.ok) {
        const result = await response.json();
        setQuoteData(result);
        setActiveTab("quote");
        setOrderSuccess(null); // Clear any previous order success messages
        
        // Track quote submission in Google Analytics
        try {
          trackQuoteSubmission({
            material: formData.material,
            quality: formData.quality,
            estimated_price: result.final_price
          });
        } catch (analyticsError) {
          console.warn('Analytics tracking failed:', analyticsError);
        }
        
        // Scroll to the instant quote section to show results
        setTimeout(() => {
          const quoteSection = document.querySelector('#instant-quote');
          if (quoteSection) {
            quoteSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Quote submission failed:', errorData);
        alert('Error submitting quote request. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting quote:', error);
      alert('Error submitting quote request. Please try again.');
    } finally {
      setLoadingQuote(false);
    }
  };

  const handleCreateOrder = async () => {
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }

    if (!quoteData) {
      alert('No quote data available');
      return;
    }

    setLoadingOrder(true);
    
    try {
      // Get filename from formData (the uploaded file name)
      const filename = formData.file ? formData.file.name : quoteData.filename || 'Custom 3D Model';
      
      // Format description with all print settings from formData (user selections)
      const description = [
        `📄 File: ${filename}`,
        `🎨 Material: ${formData.material} - ${formData.color === 'Other' ? formData.customColor : formData.color}`,
        `⚙️ Quality: ${formData.quality}`,
        `💪 Strength: ${formData.strength}`,
        `✨ Smoothing: ${formData.smoothing}`
      ].join('\n');
      
      const cartItem = {
        id: `quote-${quoteData.quote_id}`, // Unique ID for cart item
        quote_id: quoteData.quote_id,
        type: 'print_quote',
        name: `Print Order - Quote ${quoteData.quote_id.substring(0, 8)}`,
        description: description,
        price: quoteData.price_breakdown.print_cost + quoteData.price_breakdown.smoothing_cost, // Price without shipping
        image: 'https://img.icons8.com/color/150/000000/3d-printer.png', // 3D printer icon
        image_url: 'https://img.icons8.com/color/150/000000/3d-printer.png', // Cart uses image_url
        category: 'Custom 3D Print',
        manufacturer: 'On-Demand Print',
        in_stock: true,
        quantity: 1, // Default quantity
        // Store all quote data for order creation
        quote_data: {
          quote_id: quoteData.quote_id,
          filename: filename,
          material: formData.material,
          color: formData.color === 'Other' ? formData.customColor : formData.color,
          quality: formData.quality,
          strength: formData.strength,
          smoothing: formData.smoothing,
          volume: quoteData.volume,
          surface_area: quoteData.surface_area,
          complexity_score: quoteData.complexity_score,
          price_breakdown: quoteData.price_breakdown,
          estimated_time: quoteData.estimated_time,
          delivery_weeks: quoteData.delivery_weeks,
          production_time: quoteData.production_time,
          contact_email: formData.contact_email,
          units: formData.units,
          shipping_country_code: formData.country_code
        }
      };
      
      // Add to cart
      addToCart(cartItem);
      
      // Show success message and redirect to cart
      alert(`✓ Quote added to cart!\n\nYou can now proceed to checkout or continue shopping.`);
      
      // Navigate to cart
      navigate('/cart');
      
    } catch (error) {
      console.error('Error adding quote to cart:', error);
      alert('Error adding quote to cart. Please try again.');
    } finally {
      setLoadingOrder(false);
    }
  };

  const handlePayment = async (orderId) => {
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }

    setLoadingPayment(true);
    setPaymentError(null);

    try {
      // Get Firebase ID token
      const idToken = await getIdToken();
      if (!idToken) {
        setPaymentError('Authentication error. Please log in again.');
        setShowLoginModal(true);
        return;
      }

      // Get current URL for success and cancel URLs
      const currentUrl = window.location.origin + window.location.pathname;
      const successUrl = `${currentUrl}?payment=success&session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${currentUrl}?payment=cancelled`;

      // Get backend URL
      const backendUrl = BACKEND_URL;
      let apiUrl = backendUrl;
      if (apiUrl.includes('None') || !apiUrl || apiUrl === '') {
        apiUrl = '';
      }

      const response = await fetch(`${apiUrl}/api/payments/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        credentials: 'include',
        body: JSON.stringify({
          order_id: orderId,
          success_url: successUrl,
          cancel_url: cancelUrl
        }),
      });

      if (response.ok) {
        const result = await response.json();
        
        // Redirect to Stripe checkout
        if (result.checkout_url) {
          window.location.href = result.checkout_url;
        } else {
          throw new Error('No checkout URL received');
        }
      } else {
        const errorData = await response.json();
        if (response.status === 401) {
          setShowLoginModal(true);
          setPaymentError('Please log in to proceed with payment');
        } else {
          setPaymentError(`Payment error: ${errorData.detail || 'Please try again'}`);
        }
      }
    } catch (error) {
      console.error('Payment error:', error);
      setPaymentError('Error creating payment. Please try again.');
    } finally {
      setLoadingPayment(false);
    }
  };

  const handleServiceRequest = async (serviceType, formData, file = null) => {
    try {
      let endpoint;
      let body;
      let headers = {};

      if (serviceType === "2D to 3D Conversion" || serviceType === "Product Optimization") {
        if (!file) {
          alert('File upload is required for this service');
          return;
        }
        endpoint = `${API}/request/service-with-file`;
        body = new FormData();
        body.append('service_type', serviceType);
        body.append('contact_name', formData.name);
        body.append('contact_email', formData.email);
        body.append('description', formData.description);
        body.append('file', file);
      } else if (serviceType === "Design Service") {
        endpoint = `${API}/request/service-optional-file`;
        body = new FormData();
        body.append('service_type', serviceType);
        body.append('contact_name', formData.name);
        body.append('contact_email', formData.email);
        body.append('description', formData.description);
        if (file) {
          body.append('file', file);
        }
      } else {
        endpoint = `${API}/request/service`;
        headers['Content-Type'] = 'application/json';
        body = JSON.stringify({
          service_type: serviceType,
          contact_name: formData.name,
          contact_email: formData.email,
          description: formData.description,
          files_attached: false
        });
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: headers,
        body: body,
      });

      if (response.ok) {
        // Track service request in Google Analytics
        trackServiceRequest(serviceType);
        
        alert('Service request submitted successfully! We will contact you within 24 hours.');
      } else {
        alert('Error submitting service request');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error submitting service request');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header - Clean Ambiq Style */}
      <header className="bg-white/95 backdrop-blur-sm border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center">
            {/* Logo with minimum width */}
            <div className="flex items-center flex-shrink-0">
              <img 
                src="/logo-nav.png" 
                alt="Create 3D Works Logo" 
                className="h-10 sm:h-12 w-auto object-contain"
                style={{ minWidth: '120px' }}
              />
            </div>

            {/* Spacer matching menu item spacing */}
            <div className="w-8 flex-shrink-0 hidden lg:block"></div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex space-x-8 items-center">
              <button onClick={() => navigateToPage("services")} className="font-medium text-slate-600 hover:text-[#0007b5] transition-colors">Services</button>
              <button onClick={() => navigateToPage("products")} className="font-medium text-slate-600 hover:text-[#0007b5] transition-colors">Shop</button>
              <button onClick={() => navigateToPage("marketplace")} className="font-medium text-slate-600 hover:text-[#0007b5] transition-colors">Designs</button>
              <button onClick={() => navigateToPage("about")} className="font-medium text-slate-600 hover:text-[#0007b5] transition-colors">About</button>
              <button onClick={() => navigateToPage("contact")} className="font-medium text-slate-600 hover:text-[#0007b5] transition-colors">Contact</button>
            </nav>

            {/* Right-aligned Cart and Auth buttons for Desktop */}
            <div className="hidden lg:flex items-center space-x-4 ml-auto">
              {/* Shopping Cart */}
              <button 
                onClick={() => navigateToPage("cart")}
                className="relative flex items-center space-x-2 font-medium text-slate-600 hover:text-[#0007b5] transition-colors"
              >
                <ShoppingCart className="w-5 h-5" />
                {cart.length > 0 && (
                  <span className="absolute -top-2 -right-2 bg-[#0007b5] text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {cart.length}
                  </span>
                )}
                <span className="hidden sm:inline">Cart</span>
              </button>
              
              {/* Firebase Authentication */}
              {loading ? (
                <div className="flex items-center space-x-2 font-medium text-slate-600">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#0007b5]"></div>
                  <span>Loading...</span>
                </div>
              ) : isAuthenticated ? (
                <div className="flex items-center space-x-4">
                  <button 
                    onClick={() => navigateToPage("profile")}
                    className="flex items-center space-x-2 font-medium text-slate-600 hover:text-[#0007b5] transition-colors"
                  >
                    <User className="w-4 h-4" />
                    <span>{user?.name || 'Profile'}</span>
                  </button>
                  <button 
                    onClick={logout}
                    className="font-medium text-slate-600 hover:text-[#0007b5] transition-colors text-sm"
                  >
                    Sign Out
                  </button>
                </div>
              ) : (
                <button 
                  onClick={() => setShowLoginModal(true)}
                  className="flex items-center space-x-2 bg-[#0007b5] hover:bg-[#000690] text-white px-6 py-2 rounded-lg font-medium transition-colors"
                >
                  <LogIn className="w-4 h-4" />
                  <span>Sign In</span>
                </button>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="flex lg:hidden items-center gap-3 ml-auto">
              {/* Cart for mobile */}
              <button 
                onClick={() => navigateToPage("cart")}
                className="relative p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ShoppingCart className="w-6 h-6 text-slate-600" />
                {cart.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#0007b5] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
                    {cart.length}
                  </span>
                )}
              </button>

              {/* Hamburger menu button */}
              <button 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? (
                  <X className="w-6 h-6 text-slate-600" />
                ) : (
                  <div className="space-y-1.5">
                    <div className="w-6 h-0.5 bg-slate-600"></div>
                    <div className="w-6 h-0.5 bg-slate-600"></div>
                    <div className="w-6 h-0.5 bg-slate-600"></div>
                  </div>
                )}
              </button>
            </div>
          </div>

          {/* Mobile Menu Panel */}
          {mobileMenuOpen && (
            <div className="lg:hidden mt-4 pb-4 border-t border-slate-100 pt-4">
              <nav className="flex flex-col space-y-4">
                <button onClick={() => {navigateToPage("services"); setMobileMenuOpen(false);}} className="text-left font-medium text-slate-600 hover:text-[#0007b5] transition-colors py-2">Services</button>
                <button onClick={() => {navigateToPage("products"); setMobileMenuOpen(false);}} className="text-left font-medium text-slate-600 hover:text-[#0007b5] transition-colors py-2">Shop</button>
                <button onClick={() => {navigateToPage("marketplace"); setMobileMenuOpen(false);}} className="text-left font-medium text-slate-600 hover:text-[#0007b5] transition-colors py-2">Designs</button>
                <button onClick={() => {navigateToPage("about"); setMobileMenuOpen(false);}} className="text-left font-medium text-slate-600 hover:text-[#0007b5] transition-colors py-2">About</button>
                <button onClick={() => {navigateToPage("contact"); setMobileMenuOpen(false);}} className="text-left font-medium text-slate-600 hover:text-[#0007b5] transition-colors py-2">Contact</button>

                <div className="border-t border-slate-200 my-2"></div>

                {isAuthenticated ? (
                  <>
                    <button onClick={() => {navigateToPage("profile"); setMobileMenuOpen(false);}} className="text-left flex items-center space-x-2 py-2 text-slate-600 hover:text-[#0007b5] transition-colors">
                      <User className="w-4 h-4" />
                      <span>{user?.name || 'Profile'}</span>
                    </button>
                    <button onClick={() => {logout(); setMobileMenuOpen(false);}} className="text-left py-2 text-slate-600 hover:text-[#0007b5] transition-colors">Sign Out</button>
                  </>
                ) : (
                  <button 
                    onClick={() => {setShowLoginModal(true); setMobileMenuOpen(false);}}
                    className="bg-[#0007b5] hover:bg-[#000690] text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2 w-full"
                  >
                    <LogIn className="w-4 h-4" />
                    <span>Sign In</span>
                  </button>
                )}
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Global Payment Status Messages */}
      {paymentStatus === 'success' && (
        <div className="bg-brand-green-50 border-l-4 border-brand-green-500 p-4 mx-4 mt-4 rounded-r-lg">
          <div className="flex items-center">
            <Check className="w-6 h-6 text-brand-green-500 mr-3" />
            <div>
              <h3 className="text-lg font-semibold text-brand-green-800">Payment Successful! 🎉</h3>
              <p className="text-brand-green-700">Your payment has been processed successfully. Your order is now confirmed and will begin production soon.</p>
              <div className="mt-3 space-x-4">
                <button
                  onClick={() => navigateToPage("profile")}
                  className="bg-brand-green-600 text-white px-4 py-2 rounded-lg hover:bg-brand-green-700 transition-colors text-sm font-medium"
                >
                  View Order Status
                </button>
                <button
                  onClick={() => setPaymentStatus(null)}
                  className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {paymentStatus === 'checking' && (
        <div className="bg-brand-blue-50 border-l-4 border-brand-blue-500 p-4 mx-4 mt-4 rounded-r-lg">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-blue-600 mr-3"></div>
            <div>
              <h3 className="text-lg font-semibold text-brand-blue-800">Verifying Payment...</h3>
              <p className="text-brand-blue-700">Please wait while we confirm your payment with Stripe.</p>
            </div>
          </div>
        </div>
      )}

      {paymentError && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mx-4 mt-4 rounded-r-lg">
          <div className="flex items-center">
            <span className="text-red-500 text-xl mr-3">❌</span>
            <div>
              <h3 className="text-lg font-semibold text-red-800">Payment Issue</h3>
              <p className="text-red-700">{paymentError}</p>
              <button
                onClick={() => setPaymentError(null)}
                className="mt-2 bg-red-100 text-red-700 px-4 py-2 rounded-lg hover:bg-red-200 transition-colors text-sm"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section - Video Background */}
      <section className="relative min-h-screen overflow-hidden bg-slate-900">
        {/* Video Background */}
        <div className="absolute inset-0">
          <video 
            autoPlay 
            loop 
            muted 
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectFit: 'cover' }}
          >
            <source src="/C3D_Banner_Video_2.mp4" type="video/mp4" />
          </video>
          
          {/* Dark overlay for text readability */}
          <div className="absolute inset-0 bg-slate-900/75"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-32">
          <div className="text-center">
            {/* Clean Typography - Ambiq Style */}
            <div className="mb-8">
              <h1 className="font-black leading-none">
                <span className="block text-5xl md:text-7xl lg:text-8xl text-white">3D Printing</span>
                <span className="block text-4xl md:text-6xl lg:text-7xl mt-2 text-blue-400">Tailored to Your Needs</span>
              </h1>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-8 mt-6">
                <span className="text-white">Let us</span>{' '}
                <span className="text-blue-400">design your parts</span>{' '}
                <span className="text-white">or</span>{' '}
                <span className="text-blue-400">print yours.</span>
              </h2>
            </div>

            {/* Clean Description */}
            <p className="text-xl md:text-2xl text-slate-300 mb-12 max-w-4xl mx-auto leading-relaxed font-light">
              Use our <span className="text-white font-bold">instant quote tool</span> to <span className="text-white font-bold">print your own parts</span> to your specs. Don't have a 3D model? <span className="text-white font-bold">Let us design your part</span> from scratch, from a hand sketch, or convert a 2D drawing.
            </p>

            {/* Clean CTA Buttons - Ambiq Style */}
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <Button 
                size="lg" 
                className="bg-[#0007b5] hover:bg-[#000690] text-white px-12 py-4 text-lg font-semibold rounded-lg shadow-xl transition-all duration-300"
                onClick={scrollToQuoteSection}
              >
                Get Instant Quote
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
              
              <Button 
                variant="outline" 
                size="lg"
                className="border-2 border-white text-white hover:bg-white hover:text-[#0007b5] px-12 py-4 text-lg font-semibold rounded-lg transition-all duration-300"
                onClick={() => navigateToPage("marketplace")}
              >
                Explore CAD Library
                <FileText className="ml-2 w-5 h-5" />
              </Button>
              
              <Button 
                variant="outline" 
                size="lg"
                className="border-2 border-white text-white hover:bg-white hover:text-[#0007b5] px-12 py-4 text-lg font-semibold rounded-lg transition-all duration-300"
                onClick={() => navigateToPage("products")}
              >
                Explore Products
                <Package className="ml-2 w-5 h-5" />
              </Button>
            </div>

            {/* Trust Indicators - Clean Style */}
            <div className="mt-16 pt-8 border-t border-slate-700">
              <p className="text-slate-400 text-xs font-medium tracking-widest uppercase mb-6">Trusted by</p>
              <div className="flex items-center justify-center space-x-8 text-slate-500 text-base font-medium">
                <span>GOVERNMENT</span>
                <span>•</span>
                <span>UNIVERSITIES</span>
                <span>•</span>
                <span>COMMERCIAL</span>
              </div>
            </div>
          </div>
        </div>

        {/* Clean scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ChevronRight className="w-5 h-5 text-slate-400 rotate-90" />
        </div>
      </section>

      {/* Services Overview - Ambiq-Inspired Fluid Design */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          
          {/* Section Number - Ambiq Style */}
          <div className="flex items-center mb-16">
            <div className="text-8xl font-black text-slate-100 mr-8">01</div>
            <div>
              <div className="text-[#0007b5] text-sm font-medium tracking-widest uppercase mb-2">ENGINEERING EXPERTISE</div>
              <h3 className="text-4xl md:text-5xl font-black text-slate-900">
                Flexible Solutions
              </h3>
            </div>
          </div>
          
          {/* Services Grid - Two Row Layout */}
          <div className="space-y-8 mb-20">
            {/* First Row - 2 Larger Cards (Primary Services) */}
            <div className="grid md:grid-cols-2 gap-8">
            <div 
              className="group cursor-pointer h-full"
              onClick={() => {
                console.log('3D File Printing clicked'); // Debug log
                setActiveTab("upload");
                
                setTimeout(() => {
                  // Target the quote interface section where the tools are
                  const quoteSection = document.querySelector('[data-section="quote-interface"]');
                  console.log('Quote section found:', quoteSection); // Debug log
                  
                  if (quoteSection) {
                    // Calculate absolute position from top of document
                    const rect = quoteSection.getBoundingClientRect();
                    const absoluteTop = rect.top + window.pageYOffset - 100; // 100px offset for better positioning
                    
                    console.log('Current scroll position:', window.pageYOffset);
                    console.log('Element rect.top:', rect.top);
                    console.log('Calculated absolute scroll position:', absoluteTop);
                    
                    // Use window.scrollTo for more reliable scrolling
                    window.scrollTo({
                      top: absoluteTop,
                      behavior: 'smooth'
                    });
                  }
                }, 100);
              }}
            >
              <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-500 transform hover:-translate-y-1 border border-slate-100 h-full flex flex-col">
                <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-blue-100 transition-colors">
                  <Upload className="w-8 h-8 text-[#0007b5]" />
                </div>
                <h4 className="text-xl font-bold text-slate-900 mb-4">3D File Printing</h4>
                <p className="text-slate-600 leading-relaxed flex-1">
                  Professional analysis of your CAD files with material recommendations tailored to your specific use case and requirements.
                </p>
              </div>
            </div>

            <div 
              className="group cursor-pointer h-full"
              onClick={() => {
                setActiveTab("design");
                setTimeout(() => {
                  const quoteSection = document.querySelector('[data-section="quote-interface"]');
                  if (quoteSection) {
                    const rect = quoteSection.getBoundingClientRect();
                    const absoluteTop = rect.top + window.pageYOffset - 100;
                    window.scrollTo({
                      top: absoluteTop,
                      behavior: 'smooth'
                    });
                  }
                }, 100);
              }}
            >
              <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-500 transform hover:-translate-y-1 border border-slate-100 h-full flex flex-col">
                <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-blue-100 transition-colors">
                  <Zap className="w-8 h-8 text-[#0007b5]" />
                </div>
                <h4 className="text-xl font-bold text-slate-900 mb-4">Design Services</h4>
                <p className="text-slate-600 leading-relaxed flex-1">
                  Complete design-to-production services with engineering expertise in functional, cosmetic, and load-bearing applications.
                </p>
              </div>
            </div>
            </div>

            {/* Second Row - 3 Cards */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div 
              className="group cursor-pointer h-full"
              onClick={() => {
                console.log('2D to 3D Conversion clicked'); // Debug log
                setActiveTab("convert");
                
                setTimeout(() => {
                  // Target the quote interface section where the tools are
                  const quoteSection = document.querySelector('[data-section="quote-interface"]');
                  console.log('Quote section found:', quoteSection); // Debug log
                  
                  if (quoteSection) {
                    // Calculate absolute position from top of document
                    const rect = quoteSection.getBoundingClientRect();
                    const absoluteTop = rect.top + window.pageYOffset - 100; // 100px offset for better positioning
                    
                    console.log('Current scroll position:', window.pageYOffset);
                    console.log('Element rect.top:', rect.top);
                    console.log('Calculated absolute scroll position:', absoluteTop);
                    
                    // Use window.scrollTo for more reliable scrolling
                    window.scrollTo({
                      top: absoluteTop,
                      behavior: 'smooth'
                    });
                  }
                }, 100);
              }}
            >
              <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-500 transform hover:-translate-y-1 border border-slate-100 h-full flex flex-col">
                <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-blue-100 transition-colors">
                  <FileText className="w-8 h-8 text-[#0007b5]" />
                </div>
                <h4 className="text-xl font-bold text-slate-900 mb-4">2D to 3D Conversion</h4>
                <p className="text-slate-600 leading-relaxed flex-1">
                  Expert engineering services to transform technical drawings into optimized 3D models ready for production.
                </p>
              </div>
            </div>
            <div 
              className="group cursor-pointer h-full"
              onClick={() => {
                setActiveTab("optimize");
                setTimeout(() => {
                  const quoteSection = document.querySelector('[data-section="quote-interface"]');
                  if (quoteSection) {
                    const rect = quoteSection.getBoundingClientRect();
                    const absoluteTop = rect.top + window.pageYOffset - 100;
                    window.scrollTo({
                      top: absoluteTop,
                      behavior: 'smooth'
                    });
                  }
                }, 100);
              }}
            >
              <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-500 transform hover:-translate-y-1 border border-slate-100 h-full flex flex-col">
                <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-blue-100 transition-colors">
                  <Settings className="w-8 h-8 text-[#0007b5]" />
                </div>
                <h4 className="text-xl font-bold text-slate-900 mb-4">Product Optimization</h4>
                <p className="text-slate-600 leading-relaxed flex-1">
                  Engineering analysis and optimization for maximum performance, durability, and manufacturing efficiency.
                </p>
              </div>
            </div>

            <div 
              className="group cursor-pointer h-full"
              onClick={() => {
                setActiveTab("renders");
                setTimeout(() => {
                  const quoteSection = document.querySelector('[data-section="quote-interface"]');
                  if (quoteSection) {
                    const rect = quoteSection.getBoundingClientRect();
                    const absoluteTop = rect.top + window.pageYOffset - 100;
                    window.scrollTo({
                      top: absoluteTop,
                      behavior: 'smooth'
                    });
                  }
                }, 100);
              }}
            >
              <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-500 transform hover:-translate-y-1 border border-slate-100 h-full flex flex-col">
                <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-blue-100 transition-colors">
                  <Eye className="w-8 h-8 text-[#0007b5]" />
                </div>
                <h4 className="text-xl font-bold text-slate-900 mb-4">Product Renders</h4>
                <p className="text-slate-600 leading-relaxed flex-1">
                  Realistic renders of 3D printed products including layer visualization to see how your finished component will look.
                </p>
              </div>
            </div>
            </div>
          </div>

          {/* Feature Highlights - Ambiq Style */}
          <div className="text-center">
            <p className="text-slate-600 text-lg max-w-4xl mx-auto leading-relaxed">
              Engineering-driven approach with experience across prototype, cosmetic, functional, load-bearing, 
              and wrapped components. Flexible solutions for every application.
            </p>
          </div>
        </div>
      </section>

      {/* Technology Section - Ambiq-Inspired */}
      <section id="about" className="py-24 bg-slate-50 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          
          {/* Section Number - Ambiq Style */}
          <div className="flex items-center mb-16">
            <div className="text-8xl font-black text-slate-200 mr-8">02</div>
            <div>
              <div className="text-[#0007b5] text-sm font-medium tracking-widest uppercase mb-2">ENGINEERING EXPERTISE</div>
              <h3 className="text-4xl md:text-5xl font-black text-slate-900">
                Proven Experience
              </h3>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
            {/* Left Content */}
            <div className="space-y-8">
              <div>
                <h4 className="text-2xl font-bold text-slate-900 mb-4">Engineering-Driven Solutions</h4>
                <p className="text-lg text-slate-600 leading-relaxed">
                  Created by a seasoned "skunkworks team" with over 8 combined years of hands-on 3D printing experience and designing end-use parts specifically for 3D printing. We understand the technical challenges and provide solutions that work.
                </p>
              </div>
              
              <div>
                <h4 className="text-2xl font-bold text-slate-900 mb-4">Complete Application Range</h4>
                <p className="text-lg text-slate-600 leading-relaxed">
                  From prototype testing to production-grade components. Experience with cosmetic parts, functional assemblies, 
                  load-bearing structures, smoothed surfaces, painted finishes, and wrapped components.
                </p>
              </div>

              <div className="grid grid-cols-3 gap-6 pt-8">
                <div className="text-center">
                  <div className="text-3xl font-black text-slate-900 mb-1">Custom</div>
                  <div className="text-sm text-slate-600 font-medium">Design Parts</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-black text-slate-900 mb-1">3,000+ lb</div>
                  <div className="text-sm text-slate-600 font-medium">Filament Printed</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-black text-slate-900 mb-1">Every Use</div>
                  <div className="text-sm text-slate-600 font-medium">Case Covered</div>
                </div>
              </div>
            </div>

            {/* Right Content - Clean Image */}
            <div className="relative">
              <img 
                src="/home_printed_components_2.png"
                alt="Precision Mechanical Components"
                className="rounded-2xl shadow-2xl w-full h-auto object-contain"
              />
            </div>
          </div>

        </div>
      </section>

      {/* Main Service Interface - Clean Style */}
      <section className="py-24 bg-white relative overflow-hidden" data-section="quote-interface">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          
          <div id="instant-quote" className="text-center mb-16">
            <div className="text-[#0007b5] text-sm font-medium tracking-widest uppercase mb-2">GET STARTED</div>
            <h3 className="text-4xl md:text-5xl font-black text-slate-900">
              Start Your Project
            </h3>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-5 mb-12 bg-slate-50 rounded-2xl p-1 border-0">
              <TabsTrigger value="upload" className="flex items-center space-x-2 rounded-xl font-medium">
                <Upload className="w-4 h-4" />
                <span>Upload & Quote</span>
              </TabsTrigger>
              <TabsTrigger value="convert" className="flex items-center space-x-2 rounded-xl font-medium">
                <FileText className="w-4 h-4" />
                <span>2D to 3D</span>
              </TabsTrigger>
              <TabsTrigger value="design" className="flex items-center space-x-2 rounded-xl font-medium">
                <Zap className="w-4 h-4" />
                <span>Design</span>
              </TabsTrigger>
              <TabsTrigger value="optimize" className="flex items-center space-x-2 rounded-xl font-medium">
                <Settings className="w-4 h-4" />
                <span>Optimize</span>
              </TabsTrigger>
              <TabsTrigger value="renders" className="flex items-center space-x-2 rounded-xl font-medium">
                <Eye className="w-4 h-4" />
                <span>Renders</span>
              </TabsTrigger>
            </TabsList>

            {/* 3D File Upload Tab */}
            <TabsContent value="upload">
              <Card className="border-0 shadow-lg rounded-2xl">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Upload className="w-5 h-5 text-[#0007b5]" />
                    <span>Upload 3D File for Instant Quote</span>
                  </CardTitle>
                  <CardDescription>
                    Upload your 3D CAD file and customize your printing preferences to get an instant quote.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleQuoteSubmit} className="space-y-6">
                    {/* File Upload Section - Full Width */}
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="file-upload">3D File Upload</Label>
                        {formData.file ? (
                          // Show newly selected file
                          <div className="mt-2">
                            <div className="flex items-center justify-between p-4 bg-blue-50 border-2 border-blue-400 rounded-lg">
                              <div className="flex items-center space-x-3 flex-1">
                                <FileText className="h-6 w-6 text-[#0007b5] flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-semibold text-blue-900 truncate">
                                    {formData.file.name}
                                  </p>
                                  <p className="text-xs text-blue-700 mt-0.5">
                                    {(formData.file.size / 1024 / 1024).toFixed(2)} MB
                                  </p>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => document.getElementById('file-upload').click()}
                                className="ml-3 px-4 py-2 bg-white border border-[#0007b5] text-blue-700 rounded-md hover:bg-blue-50 text-sm font-medium flex-shrink-0"
                              >
                                Change File
                              </button>
                            </div>
                            <Input
                              id="file-upload"
                              type="file"
                              accept=".stl,.obj,.3mf,.ply,.amf,.step,.stp"
                              onChange={handleFileUpload}
                              className="hidden"
                            />
                          </div>
                        ) : formData.existing_quote_id && formData.preserved_filename ? (
                          // Show preserved file with option to change
                          <div className="mt-2">
                            <div className="flex items-center justify-between p-4 bg-green-50 border-2 border-green-400 rounded-lg">
                              <div className="flex items-center space-x-3 flex-1">
                                <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-semibold text-green-900 truncate">
                                    {formData.preserved_filename}
                                  </p>
                                  <p className="text-xs text-green-700 mt-0.5">
                                    File preserved from original quote
                                  </p>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => document.getElementById('file-upload').click()}
                                className="ml-3 px-4 py-2 bg-white border border-green-600 text-green-700 rounded-md hover:bg-green-50 text-sm font-medium flex-shrink-0"
                              >
                                Change File
                              </button>
                            </div>
                            <Input
                              id="file-upload"
                              type="file"
                              accept=".stl,.obj,.3mf,.ply,.amf,.step,.stp"
                              onChange={handleFileUpload}
                              className="hidden"
                            />
                            <p className="text-xs text-slate-500 mt-2">
                              Click "Change File" to upload a different file, or keep this file and continue editing other parameters.
                            </p>
                          </div>
                        ) : (
                          // Show normal file upload
                          <>
                            <Input
                              id="file-upload"
                              type="file"
                              accept=".stl,.obj,.3mf,.ply,.amf,.step,.stp"
                              onChange={handleFileUpload}
                              required={!formData.existing_quote_id}
                              className="mt-2"
                            />
                            <p className="text-sm text-slate-500 mt-1">
                              Supported: STL, OBJ, 3MF, PLY, AMF, STEP
                            </p>
                          </>
                        )}
                        
                        {/* Units Selection */}
                        <div className="mt-4">
                          <Label htmlFor="units">CAD File Units</Label>
                          <Select value={formData.units} onValueChange={(value) => setFormData({...formData, units: value})} required>
                            <SelectTrigger className="mt-2">
                              <SelectValue placeholder="Choose units" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="mm">Millimeters (mm)</SelectItem>
                              <SelectItem value="cm">Centimeters (cm)</SelectItem>
                              <SelectItem value="m">Meters (m)</SelectItem>
                              <SelectItem value="in">Inches (in)</SelectItem>
                              <SelectItem value="ft">Feet (ft)</SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-sm text-slate-500 mt-1">Select the units used in your CAD file</p>
                        </div>
                        
                        {/* 3D Model Preview */}
                        {formData.file && (
                          <div className="mt-6">
                            <ThreeDPreview 
                              file={formData.file}
                              material={formData.material}
                              strength={formData.strength}
                              quality={formData.quality}
                              units={formData.units}
                              onAnalysis={(analysisData) => {
                                console.log('3D Analysis:', analysisData);
                                setFormData(prev => ({
                                  ...prev,
                                  analysisData: analysisData
                                }));
                              }}
                              className="w-full"
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Part Section with Subheading */}
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-slate-900 mb-4 pb-2 border-b border-slate-200">Part</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="material">Material</Label>
                            <Select value={formData.material} onValueChange={(value) => setFormData({...formData, material: value})} required>
                              <SelectTrigger className="mt-2">
                                <SelectValue placeholder="Choose material" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="PLA">PLA - Standard</SelectItem>
                                <SelectItem value="PLA+">PLA+ - Reinforced</SelectItem>
                                <SelectItem value="Silk PLA">Silk PLA - Glossy finish</SelectItem>
                                <SelectItem value="Wood-fill PLA">Wood-fill PLA - Reinforced, Wood-like finish</SelectItem>
                                <SelectItem value="Metal-fill PLA">Metal-fill PLA - Reinforced, Metal-like finish</SelectItem>
                                <SelectItem value="CF PLA">CF PLA - Carbon Fiber reinforced</SelectItem>
                                <SelectItem value="HT PLA">HT PLA - High Temp PLA</SelectItem>
                                <SelectItem value="PETG">PETG</SelectItem>
                                <SelectItem value="CPE+">CPE+</SelectItem>
                                <SelectItem value="ABS">ABS</SelectItem>
                                <SelectItem value="ABS+">ABS+</SelectItem>
                                <SelectItem value="ASA">ASA</SelectItem>
                                <SelectItem value="TPU">TPU</SelectItem>
                                <SelectItem value="TPU 85A">TPU 85A</SelectItem>
                                <SelectItem value="TPU 64D">TPU 64D (semi-rigid)</SelectItem>
                                <SelectItem value="PC">PC - coming soon!</SelectItem>
                                <SelectItem value="PC-CF">PC-CF - coming soon!</SelectItem>
                                <SelectItem value="PA">PA - coming soon!</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div>
                            <Label htmlFor="quality">Print Quality</Label>
                            <Select value={formData.quality} onValueChange={(value) => setFormData({...formData, quality: value})} required>
                              <SelectTrigger className="mt-2">
                                <SelectValue placeholder="Choose quality" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Concept">Concept - Quick Validation</SelectItem>
                                <SelectItem value="Prototype">Prototype - Functional Testing</SelectItem>
                                <SelectItem value="Test/Demo">Test/Demo - Demonstration Ready</SelectItem>
                                <SelectItem value="End Use">End Use - Production Quality</SelectItem>
                                <SelectItem value="Display">Display - Premium Finish</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div>
                            <Label htmlFor="strength">Strength Requirements</Label>
                            <Select value={formData.strength} onValueChange={(value) => setFormData({...formData, strength: value})} required>
                              <SelectTrigger className="mt-2">
                                <SelectValue placeholder="Choose strength" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Cosmetic Only">Cosmetic Only - Display Model</SelectItem>
                                <SelectItem value="Reinforced">Reinforced - Light Use</SelectItem>
                                <SelectItem value="Functional">Functional - Heavy Duty</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="color">Color</Label>
                            <Select 
                              value={formData.color} 
                              onValueChange={(value) => setFormData({...formData, color: value})} 
                              required
                            >
                              <SelectTrigger className="mt-2">
                                <SelectValue placeholder="Choose color" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Any">Any</SelectItem>
                                <SelectItem value="Black">Black</SelectItem>
                                <SelectItem value="White">White</SelectItem>
                                <SelectItem value="Grey">Grey</SelectItem>
                                <SelectItem value="Red">Red</SelectItem>
                                <SelectItem value="Blue">Blue</SelectItem>
                                <SelectItem value="Other">Other (specify below)</SelectItem>
                              </SelectContent>
                            </Select>
                            
                            {formData.color === 'Other' && (
                              <div className="mt-3">
                                <Label htmlFor="customColor">Custom Color</Label>
                                <Input
                                  id="customColor"
                                  type="text"
                                  placeholder="e.g., Green, Yellow, Purple"
                                  value={formData.customColor}
                                  onChange={(e) => setFormData({...formData, customColor: e.target.value})}
                                  required
                                  className="mt-2"
                                />
                              </div>
                            )}
                          </div>

                          <div>
                            <Label htmlFor="smoothing">Surface Smoothing</Label>
                            <Select value={formData.smoothing} onValueChange={(value) => setFormData({...formData, smoothing: value})} required>
                              <SelectTrigger className="mt-2">
                                <SelectValue placeholder="Choose smoothing" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="None - As Printed">None - As Printed</SelectItem>
                                <SelectItem value="Light Sanding">Light Sanding</SelectItem>
                                <SelectItem value="Smooth Finish">Smooth Finish</SelectItem>
                                <SelectItem value="Premium Finish">Premium Finish</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div>
                            <Label htmlFor="contact_email">Contact Email</Label>
                            <Input
                              id="contact_email"
                              type="email"
                              placeholder="your@email.com"
                              value={formData.contact_email}
                              onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                              required
                              className="mt-2"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Shipping Section with Subheading - Side by Side */}
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-slate-900 mb-4 pb-2 border-b border-slate-200">Shipping</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="country_code">Shipping Destination</Label>
                          <Select value={formData.country_code} onValueChange={(value) => setFormData({...formData, country_code: value})} required>
                            <SelectTrigger className="mt-2">
                              <SelectValue placeholder="Choose destination" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="CA">Canada</SelectItem>
                              <SelectItem value="US">United States</SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-sm text-slate-500 mt-1">Shipping from Canada</p>
                        </div>
                        
                        <div>
                          <Label htmlFor="postal_code">Postal/Zip Code (Optional)</Label>
                          <Input
                            id="postal_code"
                            type="text"
                            placeholder={formData.country_code === "CA" ? "A1A 1A1" : "12345"}
                            value={formData.postal_code}
                            onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                            className="mt-2"
                            maxLength={10}
                          />
                          <p className="text-sm text-slate-500 mt-1">For accurate shipping estimate</p>
                        </div>
                      </div>
                    </div>

                    {/* Full-width Additional Notes */}
                    <div className="mb-6">
                      <Label htmlFor="notes">Additional Notes (Optional)</Label>
                      <Textarea
                        id="notes"
                        placeholder="Any special requirements or notes..."
                        value={formData.notes}
                        onChange={(e) => setFormData({...formData, notes: e.target.value})}
                        className="mt-2"
                        rows={3}
                      />
                    </div>

                    <Button 
                      type="submit" 
                      disabled={loadingQuote}
                      className="w-full bg-[#0007b5] hover:bg-[#000690] text-white py-3 text-lg font-semibold rounded-lg transition-colors"
                    >
                      {loadingQuote ? "Generating Quote..." : "Get Instant Quote"}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Quote Results Tab */}
            <TabsContent value="quote">
              {quoteData && (
                <Card className="border-0 shadow-lg rounded-2xl">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center space-x-2">
                          <Check className="w-5 h-5 text-green-600" />
                          <span>Your Quote is Ready!</span>
                        </CardTitle>
                        <CardDescription className="mt-2">
                          Quote ID: <Badge variant="outline">{quoteData.quote_id}</Badge>
                        </CardDescription>
                      </div>
                      <Button 
                        variant="outline" 
                        onClick={() => {
                          // Preserve the quote_id and filename so the file doesn't need to be re-uploaded
                          setFormData(prev => ({
                            ...prev,
                            existing_quote_id: quoteData.quote_id,
                            preserved_filename: quoteData.material_analysis?.filename || 'original file'
                          }));
                          setActiveTab("upload");
                        }}
                        className="flex items-center space-x-2"
                      >
                        <ArrowLeft className="w-4 h-4" />
                        <span>Edit Quote</span>
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold text-slate-800 mb-3">Price Breakdown</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span>Print Cost:</span>
                            <span>{formatPrice(quoteData.price_breakdown.print_cost)}</span>
                          </div>
                          {quoteData.price_breakdown.smoothing_cost > 0 && (
                            <div className="flex justify-between">
                              <span>Smoothing Cost:</span>
                              <span>{formatPrice(quoteData.price_breakdown.smoothing_cost)}</span>
                            </div>
                          )}
                          {quoteData.price_breakdown.shipping_cost && (
                            <>
                              <div className="flex justify-between">
                                <span>Shipping ({quoteData.price_breakdown.shipping_details?.package_size || 'Standard'}):</span>
                                <span>{formatPrice(quoteData.price_breakdown.shipping_cost)}</span>
                              </div>
                              {quoteData.price_breakdown.shipping_details && (
                                <div className="text-xs text-slate-500 ml-4">
                                  {quoteData.price_breakdown.shipping_details.shipping_zone} • {quoteData.price_breakdown.shipping_details.estimated_delivery_days}
                                </div>
                              )}
                            </>
                          )}
                          <Separator />
                          <div className="flex justify-between font-semibold text-lg">
                            <span>Total:</span>
                            <span className="text-[#0007b5]">{formatPrice(quoteData.estimated_price)}</span>
                          </div>
                        </div>
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-800 mb-3">Timeline</h4>
                        <div className="space-y-3">
                          <div>
                            <p className="text-sm text-slate-600">Production Time</p>
                            <p className="text-lg font-bold text-slate-700">{quoteData.estimated_time}</p>
                          </div>
                          <div>
                            <p className="text-sm text-slate-600">Delivery Estimate</p>
                            <p className="text-2xl font-bold text-slate-700">{quoteData.delivery_weeks} weeks</p>
                            <p className="text-sm text-slate-500">From order confirmation</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="pt-4">
                      {/* Add to Cart Button */}
                      <div className="space-y-3">
                        <Button 
                          onClick={handleCreateOrder}
                          disabled={loadingOrder}
                          className="w-full bg-[#0007b5] hover:bg-[#000690]"
                        >
                          {loadingOrder ? "Adding to Cart..." : "🛒 Add to Cart & Checkout"}
                        </Button>
                        
                        {!isAuthenticated && (
                          <p className="text-center text-sm text-slate-500">
                            🔒 Sign in required to add to cart
                          </p>
                        )}
                        
                        <p className="text-center text-xs text-slate-400">
                          Your quote will be saved and added to your cart. Shipping calculated at checkout.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Other Service Tabs */}
            <TabsContent value="convert">
              <ServiceRequestForm 
                title="2D to 3D Conversion Service"
                description="Upload your 2D drawings or blueprints and we'll convert them to printable 3D models."
                serviceType="2D to 3D Conversion"
                fileRequired={true}
                onSubmit={handleServiceRequest}
              />
            </TabsContent>

            <TabsContent value="design">
              <ServiceRequestForm 
                title="Custom Design Service"
                description="Describe your idea and our designers will create a custom 3D model for you."
                serviceType="Design Service"
                fileRequired={false}
                onSubmit={handleServiceRequest}
              />
            </TabsContent>

            <TabsContent value="optimize">
              <ServiceRequestForm 
                title="Product Optimization Service"
                description="Send us your 3D model and we'll optimize it for better printability and performance."
                serviceType="Product Optimization"
                fileRequired={true}
                onSubmit={handleServiceRequest}
              />
            </TabsContent>

            <TabsContent value="renders">
              <ServiceRequestForm 
                title="Product Renders Service"
                description="Get realistic renders of your 3D printed products. We'll create high-quality visualizations including layer views to show exactly how your finished component will look."
                serviceType="Product Renders"
                fileRequired={true}
                onSubmit={handleServiceRequest}
              />
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Section 03 - Build Your Own Products */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          
          {/* Section Number - Ambiq Style */}
          <div className="flex items-center mb-16">
            <div className="text-8xl font-black text-slate-100 mr-8">03</div>
            <div>
              <div className="text-[#0007b5] text-sm font-medium tracking-widest uppercase mb-2">EXPERT SUPPORT</div>
              <h3 className="text-4xl md:text-5xl font-black text-slate-900">
                Build Your Own Products
              </h3>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div className="space-y-8">
              <div>
                <h4 className="text-2xl font-bold text-slate-900 mb-4">Professional Equipment & Support</h4>
                <p className="text-lg text-slate-600 leading-relaxed mb-6">
                  Ready to bring 3D printing in-house? We offer professional-grade printers and filaments 
                  with comprehensive support to get you started. Every purchase includes 1 month of free 
                  expert consulting to ensure your success.
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-[#0007b5] rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-slate-700"><strong>Free 1-Month Support:</strong> Direct access to our engineering team for troubleshooting and optimization</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-[#0007b5] rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-slate-700"><strong>Print Quality Consulting:</strong> Help with failing prints, layer adhesion, and surface finish issues</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-[#0007b5] rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-slate-700"><strong>Material Guidance:</strong> Recommendations for your specific applications and use cases</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-[#0007b5] rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-slate-700"><strong>Setup & Calibration:</strong> Printer setup guidance and calibration support</p>
                  </div>
                </div>
              </div>
              
              <Button 
                className="bg-[#0007b5] hover:bg-[#000690] text-white px-12 py-4 text-lg font-semibold rounded-lg shadow-xl transition-all duration-300"
                onClick={() => navigateToPage("products")}
              >
                Shop Now
                <Package className="ml-2 w-5 h-5" />
              </Button>
            </div>

            {/* Right Content - Visual */}
            <div className="relative">
              <img 
                src="/images/hero-design-marketplace.png"
                alt="Professional 3D Printing Setup"
                className="rounded-2xl shadow-2xl w-full h-auto object-contain"
              />
            </div>
          </div>
        </div>
      </section>


      {/* Section 04 - CAD Library / Marketplace */}
      <section className="py-24 bg-slate-50 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          
          {/* Section Number - Ambiq Style */}
          <div className="flex items-center mb-16">
            <div className="text-8xl font-black text-slate-100 mr-8">04</div>
            <div>
              <div className="text-[#0007b5] text-sm font-medium tracking-widest uppercase mb-2">DESIGNER MARKETPLACE</div>
              <h3 className="text-4xl md:text-5xl font-black text-slate-900">
                Explore CAD Library
              </h3>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Content - Features Grid */}
            <div className="space-y-6">
              <p className="text-xl text-slate-600 leading-relaxed mb-8">
                Access our curated library of professional 3D models designed specifically for 3D printing. 
                Download files for instant printing or request custom prints directly from designers.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
                  <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-4">
                    <FileText className="w-6 h-6 text-[#0007b5]" />
                  </div>
                  <h4 className="text-lg font-bold text-slate-900 mb-2">Print-Ready Files</h4>
                  <p className="text-slate-600 text-sm">
                    Download optimized CAD files ready for immediate 3D printing with any printer.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
                  <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-4">
                    <Users className="w-6 h-6 text-[#0007b5]" />
                  </div>
                  <h4 className="text-lg font-bold text-slate-900 mb-2">Expert Designers</h4>
                  <p className="text-slate-600 text-sm">
                    Models created by experienced engineers and designers who understand 3D printing.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
                  <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-4">
                    <Package className="w-6 h-6 text-[#0007b5]" />
                  </div>
                  <h4 className="text-lg font-bold text-slate-900 mb-2">Print on Demand</h4>
                  <p className="text-slate-600 text-sm">
                    Order prints directly from the marketplace with material and finish options.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
                  <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-4">
                    <Eye className="w-6 h-6 text-[#0007b5]" />
                  </div>
                  <h4 className="text-lg font-bold text-slate-900 mb-2">3D Preview</h4>
                  <p className="text-slate-600 text-sm">
                    Interactive 3D preview of every model before download or purchase.
                  </p>
                </div>
              </div>

              <div className="pt-6">
                <Button 
                  className="bg-[#0007b5] hover:bg-[#000690] text-white px-12 py-4 text-lg font-semibold rounded-lg shadow-xl transition-all duration-300"
                  onClick={() => navigateToPage("marketplace")}
                >
                  Browse CAD Library
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Right Content - Visual */}
            <div className="relative">
              <div className="bg-gradient-to-br from-blue-50 to-slate-100 rounded-2xl p-8 shadow-xl">
                <div className="space-y-4">
                  {/* Mock design card */}
                  <div className="bg-white rounded-xl p-6 shadow-md border border-slate-200">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h5 className="font-bold text-slate-900 mb-1">Professional CAD Models</h5>
                        <p className="text-sm text-slate-500">Licensed for commercial use</p>
                      </div>
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <FileText className="w-6 h-6 text-[#0007b5]" />
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">Multiple Formats</span>
                      <span className="font-bold text-[#0007b5]">STL • 3MF • STEP</span>
                    </div>
                  </div>

                  {/* Mock stats */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-white rounded-xl p-4 text-center shadow-md">
                      <div className="text-2xl font-black text-[#0007b5] mb-1">100+</div>
                      <div className="text-xs text-slate-600">Models</div>
                    </div>
                    <div className="bg-white rounded-xl p-4 text-center shadow-md">
                      <div className="text-2xl font-black text-[#0007b5] mb-1">50+</div>
                      <div className="text-xs text-slate-600">Categories</div>
                    </div>
                    <div className="bg-white rounded-xl p-4 text-center shadow-md">
                      <div className="text-2xl font-black text-[#0007b5] mb-1">3D</div>
                      <div className="text-xs text-slate-600">Preview</div>
                    </div>
                  </div>

                  {/* Popular categories */}
                  <div className="bg-white rounded-xl p-6 shadow-md">
                    <h6 className="font-bold text-slate-900 mb-3 text-sm">Popular Categories</h6>
                    <div className="flex flex-wrap gap-2">
                      <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">Automotive</span>
                      <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">Aerospace</span>
                      <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">Robotics</span>
                      <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">Tools</span>
                      <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">Brackets</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-blue-100 rounded-2xl opacity-20"></div>
            </div>
          </div>

          {/* Additional Info Banner */}
          <div className="mt-16 bg-white rounded-2xl p-8 border border-slate-200 shadow-sm">
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-3xl font-black text-slate-900 mb-2">Instant Download</div>
                <p className="text-slate-600">Get files immediately after purchase</p>
              </div>
              <div>
                <div className="text-3xl font-black text-slate-900 mb-2">Quality Verified</div>
                <p className="text-slate-600">All models tested and print-ready</p>
              </div>
              <div>
                <div className="text-3xl font-black text-slate-900 mb-2">Designer Support</div>
                <p className="text-slate-600">Direct contact with model creators</p>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Contact Section - Clean Style */}
      <section id="contact" className="py-24 bg-slate-50 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          
          <div className="text-center mb-16">
            <div className="text-[#0007b5] text-sm font-medium tracking-widest uppercase mb-2">GET IN TOUCH</div>
            <h3 className="text-4xl md:text-5xl font-black text-slate-900">
              Contact Us
            </h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <Card className="border-0 shadow-sm hover:shadow-lg transition-all duration-300 text-center rounded-2xl">
              <CardContent className="pt-8 pb-8">
                <div className="w-16 h-16 mx-auto mb-4 bg-blue-50 rounded-2xl flex items-center justify-center">
                  <Mail className="w-8 h-8 text-[#0007b5]" />
                </div>
                <h4 className="text-xl font-bold text-slate-900 mb-2">Email Us</h4>
                <p className="text-slate-600 text-lg">hello@create3dworks.com</p>
                <p className="text-slate-500 text-sm mt-1">Response within 2 hours</p>
              </CardContent>
            </Card>
            
            <Card className="border-0 shadow-sm hover:shadow-lg transition-all duration-300 text-center rounded-2xl">
              <CardContent className="pt-8 pb-8">
                <div className="w-16 h-16 mx-auto mb-4 bg-blue-50 rounded-2xl flex items-center justify-center">
                  <Phone className="w-8 h-8 text-[#0007b5]" />
                </div>
                <h4 className="text-xl font-bold text-slate-900 mb-2">Call Us</h4>
                <p className="text-slate-600 text-lg">+1 (555) 123-4567</p>
                <p className="text-slate-500 text-sm mt-1">Mon-Fri 9AM-6PM EST</p>
              </CardContent>
            </Card>
            
            <Card className="border-0 shadow-sm hover:shadow-lg transition-all duration-300 text-center rounded-2xl">
              <CardContent className="pt-8 pb-8">
                <div className="w-16 h-16 mx-auto mb-4 bg-blue-50 rounded-2xl flex items-center justify-center">
                  <MapPin className="w-8 h-8 text-[#0007b5]" />
                </div>
                <h4 className="text-xl font-bold text-slate-900 mb-2">Visit Us</h4>
                <p className="text-slate-600 text-lg">590 Portland St - Unit 116<br />Dartmouth, NS B2W 6B7<br />Canada</p>
                <p className="text-slate-500 text-sm mt-1">Open Mon-Fri 9AM-5PM</p>
              </CardContent>
            </Card>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-[#0007b5] to-[#000690] rounded-2xl p-12 text-center">
            <h4 className="text-3xl font-bold text-white mb-4">
              Ready to Start Your Project?
            </h4>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Get an instant quote and see why thousands of engineers trust <span className="brand-name"><span className="brand-name-bold">CREATE 3D</span> Works</span>.
            </p>
            <Button 
              size="lg" 
              className="bg-white text-[#0007b5] hover:bg-blue-50 px-8 py-4 text-lg rounded-lg"
              onClick={scrollToQuoteSection}
            >
              Get Instant Quote
            </Button>
          </div>
        </div>
      </section>

      {/* Footer - Clean Ambiq Style */}
      <Footer />

      {/* Login Modal */}
      <LoginModal 
        isOpen={showLoginModal} 
        onClose={() => setShowLoginModal(false)} 
      />
    </div>
  );
};

// Service Request Form Component
const ServiceRequestForm = ({ title, description, serviceType, fileRequired, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    description: "",
    file: null
  });

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    setFormData({ ...formData, file });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (fileRequired && !formData.file) {
      alert('File upload is required for this service');
      return;
    }
    onSubmit(serviceType, formData, formData.file);
    setFormData({ name: "", email: "", description: "", file: null });
  };

  return (
    <Card className="border-0 shadow-lg rounded-2xl">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">Your Name</Label>
            <Input
              id="name"
              type="text"
              placeholder="John Doe"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              required
              className="mt-2"
            />
          </div>
          <div>
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              placeholder="john@example.com"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              required
              className="mt-2"
            />
          </div>
          <div>
            <Label htmlFor="description">Project Description</Label>
            <Textarea
              id="description"
              placeholder="Describe your project requirements..."
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              required
              className="mt-2"
              rows={4}
            />
          </div>
          {(fileRequired || serviceType === "Design Service") && (
            <div>
              <Label htmlFor="file">
                {fileRequired ? "File Upload (Required)" : "File Upload (Optional)"}
              </Label>
              <Input
                id="file"
                type="file"
                accept=".pdf,.dwg,.dxf,.stl,.obj,.3mf,.ply,.amf,.step,.stp,.jpg,.jpeg,.png"
                onChange={handleFileUpload}
                required={fileRequired}
                className="mt-2"
              />
              <p className="text-sm text-slate-500 mt-1">
                Accepted: CAD files, 3D models, images, PDFs
              </p>
              {formData.file && (
                <div className="mt-2">
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                    ✓ {formData.file.name}
                  </Badge>
                </div>
              )}
            </div>
          )}
          <Button type="submit" className="w-full bg-[#0007b5] hover:bg-[#000690]">
            Submit Request
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

// Page Components
const PageLayout = ({ title, children, navigateHome }) => (
  <div className="min-h-screen bg-white flex flex-col">
    <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          <Button onClick={navigateHome} variant="ghost" className="flex items-center space-x-2">
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Home</span>
          </Button>
          <button 
            onClick={navigateHome}
            className="absolute left-1/2 transform -translate-x-1/2 flex items-center"
          >
            <img 
              src="/logo-nav.png" 
              alt="Create 3D Works Logo" 
              className="h-10 sm:h-12 w-auto object-contain"
              style={{ minWidth: '120px' }}
            />
          </button>
        </div>
      </div>
    </header>
    <div className="flex-grow">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-slate-800 mb-8">{title}</h1>
        {children}
      </div>
    </div>
    <Footer onNavigate={navigateHome} />
  </div>
);

// About Us Page
const AboutUsPage = ({ navigateHome }) => (
  <PageLayout title={<span>About <span className="brand-name"><span className="brand-name-bold">CREATE 3D</span> Works</span></span>} navigateHome={navigateHome}>
    <div className="prose prose-lg max-w-none">
      <p className="text-xl text-slate-600 mb-8">
        <span className="brand-name"><span className="brand-name-bold">CREATE 3D</span> Works</span> is a leading provider of professional 3D printing services, serving customers worldwide with precision manufacturing solutions. 
        Founded with a mission to make advanced 3D printing accessible to everyone, we combine cutting-edge technology with expert craftsmanship 
        to deliver exceptional results for every project.
      </p>
      
      <h2 className="text-3xl font-bold text-slate-800 mb-6">Our Mission</h2>
      <p className="text-slate-600 mb-8">
        To democratize access to professional-grade 3D printing technology and empower innovators, engineers, and creators 
        to bring their ideas to life with uncompromising quality and precision.
      </p>
      
      <h2 className="text-3xl font-bold text-slate-800 mb-6">Why Choose Us</h2>
      <ul className="text-slate-600 space-y-4">
        <li>• State-of-the-art 3D printing equipment and technology</li>
        <li>• Expert team with years of experience in additive manufacturing</li>
        <li>• Wide range of materials and finishing options</li>
        <li>• Fast turnaround times without compromising quality</li>
        <li>• Competitive pricing and transparent quoting</li>
        <li>• Dedicated customer support throughout your project</li>
      </ul>
    </div>
  </PageLayout>
);

// FAQ Page
const FAQPage = ({ navigateHome }) => (
  <PageLayout title="Frequently Asked Questions" navigateHome={navigateHome}>
    <div className="space-y-8">
      <div>
        <h3 className="text-2xl font-bold text-slate-800 mb-4">What file formats do you accept?</h3>
        <p className="text-slate-600">
          We accept all major 3D file formats including STL, OBJ, 3MF, PLY, AMF, and STEP files. 
          Our system can automatically process and analyze these formats to provide instant quotes.
        </p>
      </div>
      
      <div>
        <h3 className="text-2xl font-bold text-slate-800 mb-4">How accurate are your prints?</h3>
        <p className="text-slate-600">
          Our 3D printers achieve accuracy levels of ±0.1mm for most materials and geometries. 
          We use industrial-grade equipment to ensure consistent, high-quality results.
        </p>
      </div>
      
      <div>
        <h3 className="text-2xl font-bold text-slate-800 mb-4">What materials do you offer?</h3>
        <p className="text-slate-600">
          We offer a wide range of materials including PLA, ABS, PETG, TPU, Wood Fill, Metal Fill, 
          and Carbon Fiber composites. Each material has different properties suitable for various applications.
        </p>
      </div>
      
      <div>
        <h3 className="text-2xl font-bold text-slate-800 mb-4">How long does shipping take?</h3>
        <p className="text-slate-600">
          Standard shipping takes 4-6 weeks from order confirmation. Express options are available 
          for faster delivery. Shipping times may vary based on complexity and current order volume.
        </p>
      </div>
    </div>
  </PageLayout>
);

// Terms Page
const TermsPage = ({ navigateHome }) => (
  <PageLayout title="Terms of Service" navigateHome={navigateHome}>
    <div className="prose prose-lg max-w-none text-slate-600">
      <p className="text-lg mb-8">
        By accessing and using Create 3D Works services, you agree to be bound by these Terms of Service and all applicable laws and regulations.
      </p>
      
      <h2 className="text-2xl font-bold text-slate-800 mb-4">Service Description</h2>
      <p className="mb-6">
        Create 3D Works provides 3D printing services, design consultation, and related manufacturing services. All services are subject to availability and technical feasibility.
      </p>
      
      <h2 className="text-2xl font-bold text-slate-800 mb-4">Intellectual Property</h2>
      <p className="mb-6">
        You retain all rights to your designs and files. Create 3D Works will not reproduce, distribute, or use your intellectual property without explicit permission.
      </p>
      
      <h2 className="text-2xl font-bold text-slate-800 mb-4">Quality and Accuracy</h2>
      <p className="mb-6">
        While we strive for the highest quality, 3D printing involves inherent tolerances and limitations. We guarantee reasonable commercial accuracy within stated tolerances.
      </p>
      
      <h2 className="text-2xl font-bold text-slate-800 mb-4">Payment and Pricing</h2>
      <p className="mb-6">
        Prices are quoted based on current material costs and complexity analysis. Final pricing may be adjusted for unusual complexity or material requirements.
      </p>
    </div>
  </PageLayout>
);

// File Formats Page
const FileFormatsPage = ({ navigateHome }) => (
  <PageLayout title="Supported File Formats" navigateHome={navigateHome}>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      <div>
        <h3 className="text-2xl font-bold text-slate-800 mb-4">3D Model Formats</h3>
        <ul className="space-y-4">
          <li className="flex items-center space-x-3">
            <Badge className="bg-green-100 text-green-800">STL</Badge>
            <span className="text-slate-600">Standard Tessellation Language - Most common format</span>
          </li>
          <li className="flex items-center space-x-3">
            <Badge className="bg-blue-100 text-blue-800">OBJ</Badge>
            <span className="text-slate-600">Wavefront OBJ - Supports textures and materials</span>
          </li>
          <li className="flex items-center space-x-3">
            <Badge className="bg-purple-100 text-purple-800">3MF</Badge>
            <span className="text-slate-600">3D Manufacturing Format - Microsoft standard</span>
          </li>
          <li className="flex items-center space-x-3">
            <Badge className="bg-orange-100 text-orange-800">PLY</Badge>
            <span className="text-slate-600">Stanford Triangle Format - Polygon file format</span>
          </li>
          <li className="flex items-center space-x-3">
            <Badge className="bg-red-100 text-red-800">AMF</Badge>
            <span className="text-slate-600">Additive Manufacturing File Format</span>
          </li>
          <li className="flex items-center space-x-3">
            <Badge className="bg-indigo-100 text-indigo-800">STEP</Badge>
            <span className="text-slate-600">Standard for Exchange of Product Data - CAD format</span>
          </li>
        </ul>
      </div>
      
      <div>
        <h3 className="text-2xl font-bold text-slate-800 mb-4">File Requirements</h3>
        <ul className="space-y-3 text-slate-600">
          <li>• Maximum file size: 100MB</li>
          <li>• Minimum feature size: 0.5mm</li>
          <li>• Recommended layer height: 0.1-0.3mm</li>
          <li>• Models should be watertight (closed meshes)</li>
          <li>• Complex geometries may require support structures</li>
          <li>• High-resolution models preferred for detailed parts</li>
        </ul>
      </div>
    </div>
  </PageLayout>
);

// Material Guide Page
const MaterialGuidePage = ({ navigateHome }) => (
  <PageLayout title="Material Selection Guide" navigateHome={navigateHome}>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      <Card>
        <CardHeader>
          <CardTitle className="text-green-700">PLA</CardTitle>
          <CardDescription>Biodegradable thermoplastic</CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="text-sm text-slate-600 space-y-2">
            <li>• Easy to print, low warping</li>
            <li>• Good for prototypes and display models</li>
            <li>• Print temperature: 190-220°C</li>
            <li>• Biodegradable and recyclable</li>
          </ul>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <CardTitle className="text-blue-700">ABS</CardTitle>
          <CardDescription>Durable engineering plastic</CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="text-sm text-slate-600 space-y-2">
            <li>• Strong and impact resistant</li>
            <li>• Good for functional parts</li>
            <li>• Print temperature: 220-250°C</li>
            <li>• Requires heated bed</li>
          </ul>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <CardTitle className="text-purple-700">PETG</CardTitle>
          <CardDescription>Chemical resistant plastic</CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="text-sm text-slate-600 space-y-2">
            <li>• Crystal clear transparency</li>
            <li>• Chemical and impact resistant</li>
            <li>• Print temperature: 220-250°C</li>
            <li>• Food-safe applications</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  </PageLayout>
);

// Quality Standards Page
const QualityStandardsPage = ({ navigateHome }) => (
  <PageLayout title="Quality Standards" navigateHome={navigateHome}>
    <div className="space-y-8">
      <div>
        <h3 className="text-2xl font-bold text-slate-800 mb-4">Precision Standards</h3>
        <p className="text-slate-600 mb-4">
          We maintain strict quality control processes to ensure every print meets our high standards:
        </p>
        <ul className="text-slate-600 space-y-2">
          <li>• Dimensional accuracy: ±0.1mm for most geometries</li>
          <li>• Surface finish: Ra 6.3μm standard, Ra 1.6μm with post-processing</li>
          <li>• Layer adhesion: &gt;90% of material strength</li>
          <li>• Color consistency: Delta E &lt;2 for colored materials</li>
        </ul>
      </div>
      
      <div>
        <h3 className="text-2xl font-bold text-slate-800 mb-4">Quality Control Process</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl font-bold text-[#0007b5]">1</span>
            </div>
            <h4 className="font-semibold text-slate-800 mb-2">Pre-Print Inspection</h4>
            <p className="text-slate-600 text-sm">File analysis and optimization review</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl font-bold text-[#0007b5]">2</span>
            </div>
            <h4 className="font-semibold text-slate-800 mb-2">Print Monitoring</h4>
            <p className="text-slate-600 text-sm">Real-time quality monitoring during production</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl font-bold text-[#0007b5]">3</span>
            </div>
            <h4 className="font-semibold text-slate-800 mb-2">Final Inspection</h4>
            <p className="text-slate-600 text-sm">Dimensional verification and finish quality check</p>
          </div>
        </div>
      </div>
    </div>
  </PageLayout>
);

// Shipping Info Page
const ShippingInfoPage = ({ navigateHome }) => (
  <PageLayout title="Shipping & Returns" navigateHome={navigateHome}>
    <div className="space-y-8">
      <div>
        <h3 className="text-2xl font-bold text-slate-800 mb-4">Delivery Options</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-blue-700">Standard Shipping</CardTitle>
              <CardDescription>4-6 weeks delivery</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600">
                Economical shipping option for non-urgent projects. Includes tracking and insurance coverage.
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle className="text-green-700">Express Shipping</CardTitle>
              <CardDescription>2-3 weeks delivery</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600">
                Expedited processing and shipping for time-sensitive projects. Priority handling throughout production.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
      
      <div>
        <h3 className="text-2xl font-bold text-slate-800 mb-4">Packaging</h3>
        <p className="text-slate-600">
          All parts are carefully packaged with protective materials to ensure safe delivery. 
          Fragile or complex geometries receive additional padding and custom packaging as needed.
        </p>
      </div>
    </div>
  </PageLayout>
);

// Marketplace Route Components

// Designer Application Route

// Creator Profile Route
const CreatorProfileRoute = () => {
  const { username } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [creator, setCreator] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  
  // Edit form state
  const [bio, setBio] = useState('');
  const [profilePicture, setProfilePicture] = useState(null);
  const [banner, setBanner] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Check if current user is the creator
  const isOwnProfile = user && creator && user.id === creator.user_id;

  useEffect(() => {
    fetchCreatorProfile();
  }, [username]);

  const fetchCreatorProfile = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/creators/${username}`);
      if (response.ok) {
        const data = await response.json();
        setCreator(data.creator);
        setListings(data.listings);
        setBio(data.creator.bio || '');
      } else {
        console.error('Creator not found');
      }
    } catch (error) {
      console.error('Error fetching creator:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Upload profile picture if selected
      if (profilePicture) {
        const formData = new FormData();
        formData.append('file', profilePicture);
        
        await fetch(`${BACKEND_URL}/api/creator/upload-profile-picture`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
      }

      // Upload banner if selected
      if (banner) {
        const formData = new FormData();
        formData.append('file', banner);
        
        await fetch(`${BACKEND_URL}/api/creator/upload-banner`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
      }

      // Update bio
      const bioFormData = new FormData();
      bioFormData.append('bio', bio);

      const response = await fetch(`${BACKEND_URL}/api/creator/my-profile`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        body: bioFormData
      });

      if (response.ok) {
        setIsEditing(false);
        fetchCreatorProfile(); // Refresh profile
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading creator profile...</p>
        </div>
      </div>
    );
  }

  if (!creator) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Creator Not Found</h1>
          <p className="text-gray-600 mb-4">This creator profile doesn't exist.</p>
          <Button onClick={() => navigate('/marketplace')}>Back to Marketplace</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Banner */}
      <div className="relative h-64 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600">
        {creator.banner_url && (
          <img 
            src={`${BACKEND_URL}${creator.banner_url}`} 
            alt="Banner" 
            className="w-full h-full object-cover"
          />
        )}
        {isEditing && (
          <div className="absolute bottom-4 right-4">
            <Input
              type="file"
              accept="image/*"
              onChange={(e) => setBanner(e.target.files[0])}
              className="bg-white"
            />
          </div>
        )}
      </div>

      {/* Profile Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative -mt-20 pb-8">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex flex-col md:flex-row items-start gap-6">
              {/* Profile Picture */}
              <div className="relative">
                {creator.profile_picture_url ? (
                  <img 
                    src={`${BACKEND_URL}${creator.profile_picture_url}`}
                    alt={creator.display_name}
                    className="w-32 h-32 rounded-full border-4 border-white shadow-lg object-cover"
                  />
                ) : (
                  <div className="w-32 h-32 rounded-full border-4 border-white shadow-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                    <span className="text-4xl font-bold text-white">
                      {creator.display_name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                {isEditing && (
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setProfilePicture(e.target.files[0])}
                    className="mt-2"
                  />
                )}
              </div>

              {/* Creator Info */}
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <h1 className="text-3xl font-bold text-gray-900">{creator.display_name}</h1>
                  {isOwnProfile && !isEditing && (
                    <Button onClick={() => setIsEditing(true)} variant="outline">
                      <Edit className="w-4 h-4 mr-2" />
                      Edit Profile
                    </Button>
                  )}
                  {isEditing && (
                    <div className="flex gap-2">
                      <Button onClick={handleSaveProfile} disabled={uploading}>
                        {uploading ? 'Saving...' : 'Save'}
                      </Button>
                      <Button onClick={() => setIsEditing(false)} variant="outline">
                        Cancel
                      </Button>
                    </div>
                  )}
                </div>
                <p className="text-gray-600 mb-4">@{creator.username}</p>

                {/* Bio */}
                {isEditing ? (
                  <textarea
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    placeholder="Tell us about yourself and your work..."
                    className="w-full p-3 border rounded-lg resize-none"
                    rows={4}
                  />
                ) : (
                  creator.bio && (
                    <p className="text-gray-700 leading-relaxed mb-4">{creator.bio}</p>
                  )
                )}

                {/* Stats */}
                <div className="flex gap-6 mt-4">
                  <div>
                    <span className="block text-2xl font-bold text-gray-900">{listings.length}</span>
                    <span className="text-sm text-gray-600">Designs</span>
                  </div>
                  <div>
                    <span className="block text-2xl font-bold text-gray-900">{creator.total_sales || 0}</span>
                    <span className="text-sm text-gray-600">Sales</span>
                  </div>
                  <div>
                    <span className="block text-2xl font-bold text-gray-900">
                      {new Date(creator.member_since).getFullYear()}
                    </span>
                    <span className="text-sm text-gray-600">Member Since</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Designs Grid */}
        <div className="mt-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Designs by {creator.display_name}</h2>
          
          {listings.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">No designs available yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {listings.map((listing) => (
                <Card 
                  key={listing.id} 
                  className="cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => navigate(`/marketplace/listing/${listing.id}`)}
                >
                  <div className="relative aspect-square bg-gradient-to-br from-blue-100 to-purple-100">
                    {listing.preview_images && listing.preview_images.length > 0 ? (
                      <img 
                        src={listing.preview_images[0]} 
                        alt={listing.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="w-16 h-16 text-blue-500" />
                      </div>
                    )}
                    {listing.has_3d_preview && (
                      <Badge className="absolute top-2 right-2 bg-[#0007b5] text-white text-xs">3D</Badge>
                    )}
                    {listing.listing_type === 'assembly' && (
                      <Badge className="absolute top-2 left-2 bg-purple-600 text-white text-xs">Assembly</Badge>
                    )}
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">{listing.title}</h3>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600">Personal</p>
                        <p className="font-bold text-[#0007b5]">${listing.personal_price}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">Commercial</p>
                        <p className="font-bold text-[#0007b5]">${listing.commercial_price}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export { ComingSoonPage, AppContent, CreatorProfileRoute };
